import { CalendarDays } from 'lucide-react';
import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { control } from './form';

/**
 * Datumseingabe im deutschen Format TT.MM.JJJJ – unabhängig von der Sprache des Browsers
 * (das native Datumsfeld zeigt bei englischem Chrome MM/DD/YYYY).
 * Nach außen bleibt der Wert ISO (JJJJ-MM-TT) wie bei <input type="date">.
 */

const iso = (y: number, m: number, d: number): string => {
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d ? dt.toISOString().slice(0, 10) : '';
};

/** „5.3.2025“, „05.03.25“, „05032025“ → „2025-03-05“; ungültig/leer → '' */
export const parseGermanDate = (input: string): string => {
  const t = input.trim();
  if (!t) return '';
  let m = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/.exec(t);
  if (!m) {
    const digits = /^(\d{2})(\d{2})(\d{4})$/.exec(t);
    if (digits) m = digits;
  }
  if (!m) return '';
  const year = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  return iso(year, Number(m[2]), Number(m[1]));
};

export const formatGermanDate = (value: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

/** Beim Tippen von Ziffern die Punkte ergänzen (05 → 05., 05.03 → 05.03.). */
const autoDots = (next: string, prev: string): string => {
  if (next.length < prev.length) return next; // Löschen nicht stören
  if (/^\d{2}$/.test(next) || /^\d{1,2}\.\d{2}$/.test(next)) return `${next}.`;
  return next;
};

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: string;
  onChange: (e: { target: { value: string } }) => void;
};

export function DateInput({ value, onChange, className, disabled, min, max, ...rest }: Props) {
  const [text, setText] = useState(() => formatGermanDate(value));
  const [touched, setTouched] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  // Wert von außen (z. B. Formular geladen) übernehmen, ohne laufende Eingaben zu stören
  useEffect(() => {
    if (parseGermanDate(text) !== value) setText(formatGermanDate(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalid = touched && text.trim() !== '' && !parseGermanDate(text);

  return (
    <div className={cn('relative', className)}>
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="TT.MM.JJJJ"
        maxLength={10}
        disabled={disabled}
        value={text}
        aria-invalid={rest['aria-invalid'] || invalid || undefined}
        className={cn(control, 'h-9 pr-9')}
        onChange={(e) => {
          const next = autoDots(e.target.value.replace(/[^\d.]/g, ''), text);
          setText(next);
          onChange({ target: { value: parseGermanDate(next) } });
        }}
        onBlur={(e) => {
          setTouched(true);
          const parsed = parseGermanDate(text);
          if (parsed) setText(formatGermanDate(parsed));
          rest.onBlur?.(e);
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        aria-label="Kalender öffnen"
        className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-slate-400 hover:text-slate-700 disabled:opacity-50"
        onClick={() => {
          try {
            picker.current?.showPicker();
          } catch {
            picker.current?.focus();
          }
        }}
      >
        <CalendarDays className="size-4" />
      </button>
      {/* Nur für den Kalender; unsichtbar */}
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden
        min={min}
        max={max}
        value={value}
        className="pointer-events-none absolute right-0 bottom-0 h-0 w-0 opacity-0"
        onChange={(e) => {
          setText(formatGermanDate(e.target.value));
          setTouched(false);
          onChange({ target: { value: e.target.value } });
        }}
      />
      {invalid && <p className="mt-1 text-xs text-red-600">Bitte als TT.MM.JJJJ eingeben, z. B. 05.03.2025.</p>}
    </div>
  );
}
